import {
  SELLING_UOM_DECIMAL_SHIFT,
  SellingUnitOfMeasure,
} from '@snap-types/models/enums';
import {
  priceStrategyConfigUpdateSchema,
  priceStrategyConfigWithRelationsSchema,
} from '@snap-types/models/price-strategy-config';
import {
  teamWithConfigSchema,
  teamWithConfigUpdateSchema,
} from '@snap-types/models/team';
import { z } from 'zod/v4';

import { availableSiteSchema, siteSchema } from '../site';
import { columns, columnValidators } from './columns';

/**
 * The scale every price and cost column is declared with — `Decimal(23,8)` on
 * `SnapQuotePrice`, `Decimal(20,8)`/`Decimal(22,8)` on `SnapOperation` and
 * `SnapRequirement`.
 *
 * It matters here because Postgres rounds a `numeric` to its scale on insert
 * rather than rejecting it, so a site configured past what the column can hold
 * would have its precision silently taken back by storage.
 */
const STORED_DECIMAL_SCALE = 8;

/** The most decimal places a site can quote to and still have it survive storage. */
export const maxDecimalPlacesFor = (
  sellingUnitOfMeasure: SellingUnitOfMeasure,
) => STORED_DECIMAL_SCALE - SELLING_UOM_DECIMAL_SHIFT[sellingUnitOfMeasure];

/**
 * A site selling in thousands stores per-unit values three decimal places finer
 * than it quotes them, so its configured precision has to leave that much room
 * in the column. Without the check the extra digits are silently dropped on
 * insert, undoing the rounding that put them there.
 */
export function decimalPlacesFitStorage({
  decimalPlaces,
  sellingUnitOfMeasure,
}: {
  decimalPlaces: number;
  sellingUnitOfMeasure: SellingUnitOfMeasure;
}): boolean {
  return decimalPlaces <= maxDecimalPlacesFor(sellingUnitOfMeasure);
}

/**
 * Derived from the same shift the check reads, so the ceiling quoted to the
 * user cannot drift from the one enforced. A site whose selling unit reserves
 * places gets the reason as well as the number, since its limit is lower than
 * the column's scale and nothing else on the form explains why.
 */
export function decimalPlacesLimitMessage(
  sellingUnitOfMeasure: SellingUnitOfMeasure,
): string {
  const max = maxDecimalPlacesFor(sellingUnitOfMeasure);
  const shift = SELLING_UOM_DECIMAL_SHIFT[sellingUnitOfMeasure];

  if (shift === 0) {
    return `Prices are stored to ${STORED_DECIMAL_SCALE} decimal places, so this cannot exceed ${max}`;
  }

  return `This site stores prices ${shift} decimal places finer than it quotes them, so this cannot exceed ${max}`;
}

export const snapSiteConfigSchema = z.object(columns);

export type SnapSiteConfig = z.output<typeof snapSiteConfigSchema>;

export const snapSiteConfigInsertSchema = snapSiteConfigSchema.omit({
  uuid: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

export type InsertableSnapSiteConfig = z.output<
  typeof snapSiteConfigInsertSchema
>;

export const snapSiteConfigWithRelationsSchema = z.object({
  ...columns,
  // priceStrategies: z.array(priceStrategyConfigWithRelationsSchema).default([]),
  // @NOTE(shawk): z.unknown() is a workaround for zod type inference issues
  priceStrategies: z
    .unknown()
    .pipe(z.array(priceStrategyConfigWithRelationsSchema))
    .default([]),
  site: z.object({
    ...siteSchema.pick({ uuid: true, code: true }).shape,
    teams: teamWithConfigSchema.array(),
  }),
});

export type SnapSiteConfigWithRelations = z.output<
  typeof snapSiteConfigWithRelationsSchema
>;

export const siteWithConfigSchema = z.object({
  ...availableSiteSchema.shape,
  snapSiteConfig: snapSiteConfigWithRelationsSchema,
});

export type SiteWithConfig = z.output<typeof siteWithConfigSchema>;

export const snapSiteConfigUpdateSchema = z.object(columnValidators);

export type UpdatableSnapSiteConfig = z.input<
  typeof snapSiteConfigUpdateSchema
>;

export const snapSiteConfigEditSchema = z
  .object({
    ...snapSiteConfigUpdateSchema.shape,
    priceStrategies: priceStrategyConfigUpdateSchema.array().default([]),
    site: z.object({
      ...siteSchema.pick({ uuid: true, code: true }).shape,
      teams: teamWithConfigUpdateSchema.array(),
    }),
  })
  .refine(
    (data) => data.partInsightsHighWinRate.gt(data.partInsightsLowWinRate),
    {
      message: 'High win rate must be greater than low win rate',
      path: ['partInsightsHighWinRate'],
    },
  )
  .refine(
    (data) => data.partInsightsHighWinRate.gt(data.partInsightsLowWinRate),
    {
      message: 'High win rate must be greater than low win rate',
      path: ['partInsightsLowWinRate'],
    },
  )
  .refine(
    (data) =>
      data.partInsightsLeadTimeRangeHigh.gt(data.partInsightsLeadTimeRangeLow),
    {
      message: 'High lead time range must be greater than low lead time range',
      path: ['partInsightsLeadTimeRangeHigh'],
    },
  )
  .refine(
    (data) =>
      data.partInsightsLeadTimeRangeHigh.gt(data.partInsightsLeadTimeRangeLow),
    {
      message: 'High lead time range must be greater than low lead time range',
      path: ['partInsightsLeadTimeRangeLow'],
    },
  )
  .refine(decimalPlacesFitStorage, {
    error: (issue) =>
      decimalPlacesLimitMessage(
        (issue.input as { sellingUnitOfMeasure: SellingUnitOfMeasure })
          .sellingUnitOfMeasure,
      ),
    path: ['decimalPlaces'],
  });

export type SnapSiteConfigEditFormDataUnvalidated = z.input<
  typeof snapSiteConfigEditSchema
>;

export type SnapSiteConfigEditFormData = z.output<
  typeof snapSiteConfigEditSchema
>;
